import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import subMinutes from 'date-fns/subMinutes'
import { usingGet } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { ToastUtils, DateUtils } from '@ocean/utils'
import { handleInvalidSession } from 'api'
import { EnvironmentUtils, UserUtils } from 'utils'
import { useCountdown } from 'hooks'
import { AccountTypes } from 'modules/Auth/types'
import {
  WartsilaClassroomSessionState,
  WartsilaExerciseSessionState,
  WartsilaSessionState,
  SimulatorTrainingSessionType,
  SimulatorType,
} from 'modules/simulatorTrainings/common/types'

type WartsilaSessionStateResponse = { state: WartsilaSessionState; stopDate?: Date }

const START_SEND_REQUEST_INTERVAL = 5 // Minutes
const CHECK_SESSION_STATE_INTERVAL = 10 // Seconds

export const CLASSROOM_START_ACTIVE_STATES = [WartsilaClassroomSessionState.Running]
export const CLASSROOM_STOP_ACTIVE_STATES = [WartsilaClassroomSessionState.Running]
export const CLASSROOM_ENDED_SESSION_STATES = [
  WartsilaClassroomSessionState.Completed,
  WartsilaClassroomSessionState.Canceled,
  WartsilaClassroomSessionState.Failed,
  WartsilaClassroomSessionState.Stopping,
]

export const EXERCISE_START_ACTIVE_STATES = [WartsilaExerciseSessionState.Running]
export const EXERCISE_STOP_ACTIVE_STATES = [WartsilaExerciseSessionState.Running]
export const EXERCISE_ENDED_SESSION_STATES = [
  WartsilaExerciseSessionState.Completed,
  WartsilaExerciseSessionState.Failed,
  WartsilaExerciseSessionState.Stopping,
]

type useScheduleSessionStateProps = {
  sessionId?: Guid
  startDate?: DateTime
  endDate?: DateTime
  simulatorType?: SimulatorType
  sessionType?: SimulatorTrainingSessionType
}

type ScheduleSessionData = {
  sessionState?: WartsilaSessionState
  resetCheckSessionState: Callback
  startingCountdown?: number
  runningCountdown?: string
  isFailed?: boolean
}

const useScheduleSessionState = (props: useScheduleSessionStateProps): ScheduleSessionData => {
  const dispatch = useDispatch()
  const [sessionState, setSessionState] = useState<WartsilaSessionState>()
  const [isFailed, setIsFailed] = useState<boolean>()
  const [checkSessionStateInterval, setCheckSessionStateInterval] = useState<
    NodeJS.Timeout | undefined
  >()
  const {
    sessionId,
    startDate,
    endDate,
    sessionType = SimulatorTrainingSessionType.SimulatorEnvironment,
    simulatorType = SimulatorType.Training,
  } = props
  const { countdown: startingCountdown, start: startStartingCountdown } = useCountdown()
  const {
    formattedCountdown: runningCountdown,
    start: startRunningCountdown,
    stop: stopRunningCountdown,
  } = useCountdown()

  const { type } = UserUtils.getUserAccountInfo()
  const isSpecialUser = type === AccountTypes.SpecialUser
  const { instructorLedStartSessionAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')
  const isNeedCheckScheduleSession =
    (!sessionState || !checkEndedSession(sessionState)) &&
    !checkSessionStateInterval &&
    sessionId &&
    startDate &&
    endDate &&
    // We should check this permission only for SpecialUser
    (!isSpecialUser || instructorLedStartSessionAvailable) &&
    sessionType === SimulatorTrainingSessionType.SimulatorEnvironment

  const apiEndpoint =
    simulatorType === SimulatorType.Training
      ? 'GetSimulatorTrainingScheduleSessionState'
      : 'GetSimulatorExerciseScheduleSession'

  function checkEndedSession(state?: WartsilaSessionState) {
    if (simulatorType === SimulatorType.Training)
      return CLASSROOM_ENDED_SESSION_STATES.includes(state as WartsilaClassroomSessionState)
    return EXERCISE_ENDED_SESSION_STATES.includes(state as WartsilaExerciseSessionState)
  }

  function checkStartingSession(state?: WartsilaSessionState) {
    if (simulatorType === SimulatorType.Training)
      return state === WartsilaClassroomSessionState.Starting
    return state === WartsilaExerciseSessionState.Starting
  }

  function checkRunningSession(state?: WartsilaSessionState) {
    if (simulatorType === SimulatorType.Training)
      return state === WartsilaClassroomSessionState.Running
    return state === WartsilaExerciseSessionState.Running
  }

  function checkFailedSession(state?: WartsilaSessionState) {
    if (simulatorType === SimulatorType.Training)
      return state === WartsilaClassroomSessionState.Failed
    return state === WartsilaExerciseSessionState.Failed
  }

  const prevState = usePrevious(sessionState)

  const clearCheckSessionStateInterval = useCallback(() => {
    // Clear old check interval
    if (checkSessionStateInterval) {
      clearInterval(checkSessionStateInterval)
      setCheckSessionStateInterval(undefined)
    }
  }, [checkSessionStateInterval])

  const resetCheckSessionState = useCallback(() => {
    clearCheckSessionStateInterval()
    setSessionState(undefined)
  }, [clearCheckSessionStateInterval])

  const checkSessionState = useCallback(async () => {
    const scheduleStateResponse = await usingGet(
      `SimulatorTrainings/${apiEndpoint}?sessionId=${sessionId}`
    )
    const { content, error } = scheduleStateResponse
    const { state, stopDate } = content || ({} as WartsilaSessionStateResponse)

    if (error) {
      const { error: errorMessage, status: responseStatus } = error

      // Handle invalid session error
      if (responseStatus === 401) {
        handleInvalidSession(errorMessage, dispatch)
        return
      }

      startStartingCountdown(CHECK_SESSION_STATE_INTERVAL)
      return ToastUtils.error(errorMessage)
    }

    setSessionState(state)

    // Stop checking state
    if (checkEndedSession(state)) savedResetCheckSessionStateCallback.current()
    // Start/stop countdowns
    if (checkStartingSession(state)) startStartingCountdown(CHECK_SESSION_STATE_INTERVAL)
    if (checkRunningSession(state) && prevState !== state && stopDate && !isSpecialUser) {
      const duration = (new Date(stopDate).getTime() - DateUtils.getDateNowGMT()) / 1000
      if (duration <= 0) return
      startRunningCountdown(duration)
    }
    if (prevState !== state && checkRunningSession(prevState)) {
      stopRunningCountdown()
    }
    // Check if it is 'failed'
    setIsFailed(checkFailedSession(state))
  }, [checkSessionStateInterval, sessionId, prevState]) // eslint-disable-line react-hooks/exhaustive-deps

  const savedCheckSessionStateCallback = useRef(checkSessionState)
  const savedResetCheckSessionStateCallback = useRef(resetCheckSessionState)

  // Remember the latest callback if it changes.
  useEffect(() => {
    savedCheckSessionStateCallback.current = checkSessionState
  }, [checkSessionState])

  // Remember the latest callback if it changes.
  useEffect(() => {
    savedResetCheckSessionStateCallback.current = resetCheckSessionState
  }, [resetCheckSessionState])

  useEffect(() => {
    if (!isNeedCheckScheduleSession || !startDate || !endDate) return

    const intervalToStartCheck = DateUtils.getIntervalFromNow(
      subMinutes(DateUtils.convertUTCDateToLocalDate(startDate), START_SEND_REQUEST_INTERVAL)
    )
    const { years, months, days, hours = 0, minutes = 0 } = intervalToStartCheck
    if (
      years === 0 &&
      months === 0 &&
      days === 0 && // If years/months/days > 0, the status check is ignored
      (hours !== 0 ||
        minutes !== 0 || // Session will run soon, so we need to check it
        DateUtils.convertUTCDateToLocalDate(endDate) >= new Date()) // Session is still running, so we should check it
    ) {
      const isSessionRunning = hours + minutes === 0
      if (!checkSessionStateInterval && isSessionRunning) checkSessionState()

      const startCheck = hours * 60 * 60 * 1000 + minutes * 60 * 1000 + 100
      setTimeout(() => {
        // Check interval
        const checkStateInterval = setInterval(
          () => savedCheckSessionStateCallback.current(),
          CHECK_SESSION_STATE_INTERVAL * 1000
        )

        clearCheckSessionStateInterval()
        setCheckSessionStateInterval(checkStateInterval)
      }, startCheck)
    }
  }, [sessionId, startDate, endDate, resetCheckSessionState]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevSessionId = usePrevious(sessionId)

  useEffect(() => {
    if (prevSessionId && !sessionId && checkSessionStateInterval) {
      resetCheckSessionState()
    }
  }, [prevSessionId, sessionId, checkSessionStateInterval, resetCheckSessionState])

  useEffect(() => {
    setIsFailed(false)
    return () => {
      savedResetCheckSessionStateCallback.current()
      setIsFailed(false)
    }
  }, [])

  return { sessionState, resetCheckSessionState, startingCountdown, runningCountdown, isFailed }
}

export default useScheduleSessionState
